import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { CheckCircle, XCircle, ArrowsClockwise } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

export default function BoutiquePaiement() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", attempt: 0, data: null, error: null });
  const polledRef = useRef(false);

  useEffect(() => {
    document.title = "Paiement — Boutique Gladiateurs du Nord";
    if (!sessionId) {
      setState({ phase: "error", error: "session_id manquant dans l'URL." });
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const r = await api.get(`/shop/checkout/status/${sessionId}`);
        const d = r.data;
        if (d.payment_status === "paid") {
          setState({ phase: "success", attempt, data: d });
          return;
        }
        if (d.status === "expired") {
          setState({ phase: "expired", attempt, data: d });
          return;
        }
        setState(s => ({ ...s, attempt, data: d }));
        if (attempt >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempt, data: d });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", error: e?.response?.data?.detail || "Erreur de vérification." });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background" data-testid="paiement-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 hard-border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <Link to="/boutique" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-boutique">← Retour boutique</Link>
        </div>
      </nav>
      <div className="max-w-2xl mx-auto px-6 py-16">
        {state.phase === "polling" && (
          <div className="hard-border bg-white p-8 space-y-4" data-testid="paiement-polling">
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={36} className="animate-spin text-[#D90429]" />
              <div>
                <div className="font-display font-black text-2xl">Vérification du paiement…</div>
                <div className="text-sm text-muted-foreground">Tentative {state.attempt}/{MAX_ATTEMPTS}</div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">Ne ferme pas cette page — confirmation en cours.</div>
          </div>
        )}
        {state.phase === "success" && (
          <div className="hard-border bg-white p-8 space-y-6" data-testid="paiement-success">
            <div className="flex items-center gap-3">
              <CheckCircle size={48} weight="fill" className="text-[#0A8754]" />
              <div>
                <div className="ticker text-muted-foreground">PAIEMENT CONFIRMÉ</div>
                <div className="font-display font-black text-3xl">{state.data?.order_no || "—"}</div>
              </div>
            </div>
            <div className="text-sm">
              Montant payé : <strong className="tabular-nums">{((state.data?.amount_total || 0) / 100).toFixed(2)} {(state.data?.currency || "cad").toUpperCase()}</strong>
            </div>
            <div className="bg-[#FFF6E5] hard-border p-4 text-sm">
              <div className="font-bold mb-1">📍 Récupération sur place — 16 mai 2026</div>
              <div>Présente ton numéro <strong>{state.data?.order_no}</strong> au kiosque boutique. T'as plus rien à payer sur place.</div>
            </div>
            <Link to="/boutique" className="inline-block bg-foreground text-white px-5 py-2.5 ticker hover:bg-black transition-colors" data-testid="back-to-boutique">Retour boutique</Link>
          </div>
        )}
        {state.phase === "timeout" && (
          <div className="hard-border bg-white p-8 space-y-4" data-testid="paiement-timeout">
            <div className="font-display font-black text-2xl">Délai dépassé</div>
            <div className="text-sm">Le paiement n'est pas encore confirmé après {MAX_ATTEMPTS} vérifications. Ta carte n'a probablement pas été débitée. Si elle a été débitée, contacte-nous via Facebook @impactkickboxing avec ton numéro <strong>{state.data?.order_no}</strong>.</div>
            <Link to="/boutique" className="inline-block hard-border px-5 py-2.5 ticker hover:bg-muted transition-colors">Retour boutique</Link>
          </div>
        )}
        {state.phase === "expired" && (
          <div className="hard-border bg-white p-8 space-y-4" data-testid="paiement-expired">
            <div className="flex items-center gap-3">
              <XCircle size={36} weight="fill" className="text-[#D90429]" />
              <div className="font-display font-black text-2xl">Session expirée</div>
            </div>
            <div className="text-sm">Cette session de paiement n'est plus valide. Recommence depuis la boutique.</div>
            <Link to="/boutique" className="inline-block bg-[#D90429] text-white px-5 py-2.5 ticker hover:bg-[#BF0424] transition-colors">Recommencer</Link>
          </div>
        )}
        {state.phase === "error" && (
          <div className="hard-border bg-white p-8 space-y-4" data-testid="paiement-error">
            <div className="flex items-center gap-3">
              <XCircle size={36} weight="fill" className="text-[#D90429]" />
              <div className="font-display font-black text-2xl">Erreur</div>
            </div>
            <div className="text-sm">{state.error || "Erreur inconnue."}</div>
            <Link to="/boutique" className="inline-block hard-border px-5 py-2.5 ticker hover:bg-muted transition-colors">Retour boutique</Link>
          </div>
        )}
      </div>
    </div>
  );
}
